import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLocalStorage } from "./useLocalStorage";
import axiosClient from "../utils/axiosClient";
import { useTranslation } from "react-i18next";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useLocalStorage("user", null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);

  const location = useLocation();

  // setUser writes to localStorage synchronously, so axiosClient's
  // interceptor always picks up the token from the most recent call.
  const fetchMe = () => axiosClient.get("/user/me");

  const value = useMemo(() => {
    // Re-fetches /user/me so fields the login response may omit (e.g.
    // chat_user_token) are always present, even on repeated logins within
    // the same SPA session (no full reload to re-run the mount effect).
    const login = async (data, navigateTo) => {
      setUser(data);
      try {
        const response = await fetchMe();
        setUser({ ...data, user: { ...response.data } });
      } catch {
        // Keep the login response as-is; the mount-time effect / route
        // guards will handle an invalid token if that's the actual issue.
      }
      navigate(navigateTo);
    };

    const logout = () => {
      setUser(null);
      navigate("/login");
    };

    // Refreshes user.user (e.g. invitations) without a full page reload or re-login.
    const refetchUser = async () => {
      const response = await fetchMe();
      setUser({ ...user, user: { ...response.data } });
      return response.data;
    };

    return {
      user,
      login,
      logout,
      refetchUser,
    };
  }, [user, navigate, setUser]);

  const unguardPaths = ["/login"];

  useEffect(() => {
    if (user?.token && !unguardPaths.includes(location.pathname)) {
      fetchMe()
        .then((response) => {
          setUser({ ...user, user: { ...response.data } });
        })
        .catch(() => {
          setUser(null);
          navigate("/login");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <center>
          <h2>{t("loading")}</h2>
        </center>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
